/**
 * ActivationPage - First customer interaction after Stripe checkout or invitation
 *
 * Part of White-Glove Onboarding: This page appears BEFORE the dashboard,
 * enabling the "data-ready-before-first-login" experience (Dan Martell activation).
 *
 * ONBOARDING GATE: For self-service subscribers (subscription.state === 'onboarding'),
 * the dashboard is structurally unreachable. AppLayout redirects here. The user stays
 * on this page until data is imported, processed, AND approved by a founder.
 *
 * White-glove companies (no subscription doc) use the legacy activationComplete flag.
 *
 * Flow:
 * 1. User lands here (from SubscriptionSuccessPage or AcceptInvitationPage)
 * 2. Imports commission data via Email Scout OR Inline Upload
 * 3. Files are processed by pipeline -> triggerAggregation -> sets onboarding.dataProcessed
 * 4. Founder reviews in admin portal and clicks "Approve & Activate"
 * 5. Subscription transitions ONBOARDING -> ACTIVE (real-time listener detects this)
 * 6. User is shown success message -> navigates to populated dashboard
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LEGAL_URLS } from '../../config/legalUrls';
import { doc, getDoc, updateDoc, onSnapshot, serverTimestamp, collection, getDocs, query } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, auth, functions } from '../../lib/firebase';
import { formatUploadDisplayName } from '@valutten/shared-utils';
import {
  Mail,
  ArrowRight,
  AlertTriangle,
  CheckCircle,
  Upload,
  X,
  Loader2,
  Database,
  Shield,
  Plus,
  FileUp,
  ExternalLink,
  File,
  Clock,
} from 'lucide-react';
import EmailScoutScanner from '../../components/email/EmailScoutScanner';
import { KNOWN_COMMISSION_SENDERS } from '../../utils/gmailScout';

interface ActivationStatus {
  activationComplete: boolean;
  activationMethod?: 'email_scout' | 'manual_upload' | 'skipped';
  activatedAt?: any;
  activatedBy?: string;
  onboarding?: {
    dataProcessed?: boolean;
    dataProcessedAt?: any;
  };
}

interface UploadFileEntry {
  file: File;
  status: 'pending' | 'uploading' | 'done' | 'error';
  error?: string;
}

const ACCEPTED_EXTENSIONS = ['.csv', '.xlsx', '.xls', '.pdf'];
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB

const ActivationPage: React.FC = () => {
  const navigate = useNavigate();
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showSkipWarning, setShowSkipWarning] = useState(false);
  const [importComplete, setImportComplete] = useState(false);
  const [importResults, setImportResults] = useState<{
    filesImported: number;
    filesDuplicate: number;
    filesErrored: number;
  } | null>(null);

  // Subscription state tracking (real-time)
  const [subscriptionState, setSubscriptionState] = useState<string | null>(null);
  const [isSubscriptionActive, setIsSubscriptionActive] = useState(false);
  // Whether this company has a subscription doc (self-service) vs white-glove (no doc)
  const [hasSubscription, setHasSubscription] = useState<boolean | null>(null);

  // Onboarding data processed flag (set by pipeline, before founder approval)
  const [dataProcessed, setDataProcessed] = useState(false);

  // Import method selection
  const [activeMethod, setActiveMethod] = useState<'gmail' | 'upload' | null>(null);

  // Custom sender email state
  const [customEmails, setCustomEmails] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const [emailInputError, setEmailInputError] = useState<string | null>(null);

  // Inline upload state
  const [uploadFiles, setUploadFiles] = useState<UploadFileEntry[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Auto-detected office/template for upload
  const [headOfficeId, setHeadOfficeId] = useState<string | null>(null);
  const [firstTemplateId, setFirstTemplateId] = useState<string | null>(null);

  // Get current user's company ID
  useEffect(() => {
    const checkAuth = async () => {
      const user = auth.currentUser;
      if (!user) {
        navigate('/login');
        return;
      }

      // Get user's company ID from their profile or claims
      const idToken = await user.getIdTokenResult();
      const claims = idToken.claims;

      if (claims.companyId) {
        setCompanyId(claims.companyId as string);

        // Check if this is a white-glove company (already activated)
        const companyRef = doc(db, 'companies', claims.companyId as string);
        const companyDoc = await getDoc(companyRef);

        if (companyDoc.exists()) {
          const data = companyDoc.data() as ActivationStatus;
          if (data.activationComplete) {
            // Already activated via white-glove process, go to dashboard
            navigate('/');
            return;
          }
        }
      } else {
        // No company ID - shouldn't happen, redirect to login
        console.error('[ActivationPage] No companyId in claims');
        navigate('/login');
        return;
      }

      setLoading(false);
    };

    checkAuth();
  }, [navigate]);

  // Fetch head office and first template for auto-detection
  useEffect(() => {
    if (!companyId) return;

    const fetchUploadDefaults = async () => {
      try {
        // Get first office (head office)
        const officesSnap = await getDocs(
          query(collection(db, 'companies', companyId, 'offices'))
        );
        if (!officesSnap.empty) {
          // Prefer head office, else first
          const headOffice = officesSnap.docs.find(d => d.data().isHeadOffice);
          setHeadOfficeId(headOffice?.id || officesSnap.docs[0].id);
        }

        // Get first enabled template
        const companyDoc = await getDoc(doc(db, 'companies', companyId));
        const enabledTemplateIds: string[] = companyDoc.data()?.enabledTemplateIds || [];
        if (enabledTemplateIds.length > 0) {
          setFirstTemplateId(enabledTemplateIds[0]);
        }
      } catch (err) {
        console.error('[ActivationPage] Failed to fetch upload defaults:', err);
      }
    };

    fetchUploadDefaults();
  }, [companyId]);

  // Real-time listener for subscription state AND company doc (dataProcessed)
  useEffect(() => {
    if (!companyId) return;

    const subscriptionRef = doc(db, 'companies', companyId, 'subscription', 'config');

    const unsubscribe = onSnapshot(
      subscriptionRef,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          const state = data?.state || null;
          setSubscriptionState(state);
          setHasSubscription(true);

          // When subscription becomes ACTIVE, founder has approved
          if (state === 'active') {
            setIsSubscriptionActive(true);
            console.log('[ActivationPage] Subscription is now ACTIVE — founder approved');
          }
        } else {
          // No subscription doc = white-glove company
          setHasSubscription(false);
          setSubscriptionState(null);
        }
      },
      (err) => {
        console.warn('[ActivationPage] Subscription listener error (degrading gracefully):', err);
        // On transient errors, retain previous state instead of flipping to false.
        // Only treat as white-glove if we never got a subscription reading.
        // This prevents a fail-open bypass where transient errors enable "skip to dashboard".
        setHasSubscription((prev) => prev === null ? false : prev);
      }
    );

    return () => unsubscribe();
  }, [companyId]);

  // Listen to company doc for onboarding.dataProcessed flag + template assignment + commission emails
  useEffect(() => {
    if (!companyId) return;

    const companyRef = doc(db, 'companies', companyId);
    const unsubscribe = onSnapshot(companyRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        if (data?.onboarding?.dataProcessed === true) {
          setDataProcessed(true);
        }

        // Reactive template detection: when founder assigns a template via admin portal,
        // future uploads switch from staging to standard pipeline path
        const ids = data?.enabledTemplateIds || [];
        if (ids.length > 0 && !firstTemplateId) {
          setFirstTemplateId(ids[0]);
          console.log('[ActivationPage] Template detected from company doc:', ids[0]);
        }

        // Pre-populate commission emails from company doc (set during checkout)
        const storedEmails: string[] = data?.commissionEmails || [];
        if (storedEmails.length > 0 && customEmails.length === 0) {
          setCustomEmails(storedEmails);
        }
      }
    });

    return () => unsubscribe();
  }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-redirect to dashboard if subscription becomes ACTIVE while user hasn't
  // imported yet (e.g., data was uploaded through another path, or user navigated
  // here directly after activation was already complete)
  useEffect(() => {
    if (isSubscriptionActive && !importComplete && !loading) {
      console.log('[ActivationPage] Subscription already ACTIVE — redirecting to dashboard');
      navigate('/');
    }
  }, [isSubscriptionActive, importComplete, loading, navigate]);

  // Handle successful Email Scout import
  const handleImportComplete = async (results: {
    filesFound: number;
    filesImported: number;
    filesDuplicate: number;
    filesErrored: number;
    errors: string[];
  }) => {
    if (!companyId) return;

    setImportResults({
      filesImported: results.filesImported,
      filesDuplicate: results.filesDuplicate,
      filesErrored: results.filesErrored,
    });

    // Mark activation method (but NOT activationComplete — founder must approve)
    try {
      const companyRef = doc(db, 'companies', companyId);
      await updateDoc(companyRef, {
        activationMethod: 'email_scout',
        activatedBy: auth.currentUser?.uid,
        'activation.filesImported': results.filesImported,
        'activation.filesDuplicate': results.filesDuplicate,
        'activation.filesErrored': results.filesErrored,
      });

      setImportComplete(true);
    } catch (err) {
      console.error('[ActivationPage] Failed to update activation status:', err);
      // Still show the processing state - the import worked
      setImportComplete(true);
    }
  };

  // Navigate to dashboard (only when data is ready)
  const handleContinue = async () => {
    if (!companyId) return;

    // Mark activation as complete before navigating
    try {
      const companyRef = doc(db, 'companies', companyId);
      await updateDoc(companyRef, {
        activationComplete: true,
        activatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('[ActivationPage] Failed to mark activation complete:', err);
      // Non-fatal — continue to dashboard
    }

    navigate('/');
  };

  // Handle skip — for white-glove companies only, or when subscription
  // doc does not exist. Self-service users cannot skip to dashboard.
  const handleSkipForWhiteGlove = async () => {
    if (!companyId) return;

    try {
      const companyRef = doc(db, 'companies', companyId);
      await updateDoc(companyRef, {
        activationComplete: true,
        activationMethod: 'skipped',
        activatedAt: serverTimestamp(),
        activatedBy: auth.currentUser?.uid,
      });

      navigate('/');
    } catch (err) {
      console.error('[ActivationPage] Failed to skip activation:', err);
    }
  };

  // Custom email management
  const handleAddEmail = () => {
    const trimmed = emailInput.trim().toLowerCase();
    if (!trimmed) return;

    // Basic email validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setEmailInputError('Please enter a valid email address');
      return;
    }

    if (customEmails.includes(trimmed)) {
      setEmailInputError('This email has already been added');
      return;
    }

    setCustomEmails((prev) => [...prev, trimmed]);
    setEmailInput('');
    setEmailInputError(null);
  };

  const handleRemoveEmail = (email: string) => {
    setCustomEmails((prev) => prev.filter((e) => e !== email));
  };

  // ============================================
  // INLINE UPLOAD LOGIC
  // ============================================

  const isValidFile = (file: File): string | null => {
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!ACCEPTED_EXTENSIONS.includes(ext)) {
      return `Unsupported file type: ${ext}. Accepted: ${ACCEPTED_EXTENSIONS.join(', ')}`;
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum: 50 MB`;
    }
    return null;
  };

  const addFiles = useCallback((files: FileList | File[]) => {
    const newEntries: UploadFileEntry[] = [];
    for (const file of Array.from(files)) {
      const error = isValidFile(file);
      newEntries.push({
        file,
        status: error ? 'error' : 'pending',
        error: error || undefined,
      });
    }
    setUploadFiles((prev) => [...prev, ...newEntries]);
  }, []);

  const removeFile = (index: number) => {
    setUploadFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      addFiles(e.target.files);
      // Reset input so same file can be re-selected
      e.target.value = '';
    }
  };

  const getContentType = (file: File): string => {
    if (file.name.endsWith('.csv')) return 'text/csv';
    if (file.name.endsWith('.xlsx')) return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    if (file.name.endsWith('.xls')) return 'application/vnd.ms-excel';
    if (file.name.endsWith('.pdf')) return 'application/octet-stream';
    return 'application/octet-stream';
  };

  // Whether we're in staging mode (no template → files staged for founder review)
  const isStagingMode = !firstTemplateId;

  const handleUploadAll = async () => {
    if (!companyId || !headOfficeId) return;

    const pendingFiles = uploadFiles.filter((f) => f.status === 'pending');
    if (pendingFiles.length === 0) return;

    setIsUploading(true);

    // Activation page ALWAYS stages — founder assigns templates after review
    const endpointName = 'issueOnboardingStagingUrl';
    const issueUrl = httpsCallable<any, any>(functions, endpointName);
    const confirmStaging = httpsCallable<any, any>(functions, 'confirmStagingUpload');
    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < uploadFiles.length; i++) {
      const entry = uploadFiles[i];
      if (entry.status !== 'pending') continue;

      // Update status to uploading
      setUploadFiles((prev) =>
        prev.map((f, idx) => (idx === i ? { ...f, status: 'uploading' as const } : f))
      );

      try {
        // Generate compliant filename
        const compliantName = formatUploadDisplayName(entry.file.name);

        // Build payload based on mode
        const payload: any = {
          companyId,
          officeId: headOfficeId,
          fileName: compliantName,
          originalFileName: entry.file.name,
          fileSize: entry.file.size,
          contentType: getContentType(entry.file),
        };

        // 1. Get signed URL
        const result = await issueUrl(payload);
        const { signedUrl } = result.data;

        // 2. Upload file via PUT
        const uploadResponse = await fetch(signedUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': getContentType(entry.file),
          },
          body: entry.file,
        });

        if (!uploadResponse.ok) {
          throw new Error(`Upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
        }

        // 3. Confirm upload so dedup recognizes it
        try {
          await confirmStaging({ uploadId: result.data.uploadId, companyId });
        } catch (confirmErr) {
          // Non-fatal — file is uploaded, just won't dedup on re-scan
          console.warn('[ActivationPage] Confirm staging failed:', confirmErr);
        }

        // 4. Mark as done
        setUploadFiles((prev) =>
          prev.map((f, idx) => (idx === i ? { ...f, status: 'done' as const } : f))
        );
        successCount++;
      } catch (err: any) {
        const errorMsg = err?.message || 'Upload failed';
        setUploadFiles((prev) =>
          prev.map((f, idx) =>
            idx === i ? { ...f, status: 'error' as const, error: errorMsg } : f
          )
        );
        errorCount++;
        console.error(`[ActivationPage] Upload failed for ${entry.file.name}:`, err);
      }
    }

    setIsUploading(false);

    // If at least one file succeeded, mark import complete
    if (successCount > 0) {
      try {
        const companyRef = doc(db, 'companies', companyId);
        await updateDoc(companyRef, {
          activationMethod: 'manual_upload_staged',
          activatedBy: auth.currentUser?.uid,
          'activation.filesImported': successCount,
          'activation.filesErrored': errorCount,
        });
      } catch (err) {
        console.error('[ActivationPage] Failed to update activation method:', err);
      }

      setImportResults({
        filesImported: successCount,
        filesDuplicate: 0,
        filesErrored: errorCount,
      });
      setImportComplete(true);
    }
  };

  // Determine if user can proceed to dashboard
  // Self-service: only when subscription state is ACTIVE
  // White-glove (no subscription): can skip freely
  const canProceedToDashboard = hasSubscription === false || isSubscriptionActive;

  // Determine if user is waiting for data processing / founder review
  // Staging mode (no template): skip "Processing" state — go straight to "Under Review"
  const isWaitingForProcessing = importComplete && hasSubscription === true && !isSubscriptionActive && !dataProcessed && !isStagingMode;
  const isWaitingForReview = importComplete && hasSubscription === true && !isSubscriptionActive && (dataProcessed || isStagingMode);

  const pendingFileCount = uploadFiles.filter((f) => f.status === 'pending').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <Loader2 className="animate-spin text-gray-400" size={48} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-4 sm:p-8 text-white">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 mb-4">
            <Database size={32} />
          </div>
          <h1 className="text-3xl font-bold mb-2">Welcome to Valutten!</h1>
          <p className="text-gray-400 text-lg">
            Import your commission data to build your dashboard.
          </p>
        </div>

        {/* STATE: Data processing complete — subscription is ACTIVE */}
        {importComplete && canProceedToDashboard && (
          <div className="bg-green-900/30 border border-green-500/30 rounded-2xl p-8 text-center">
            <CheckCircle className="mx-auto text-green-400 mb-4" size={64} />
            <h2 className="text-2xl font-bold text-green-300 mb-2">
              Your Dashboard is Ready!
            </h2>
            <p className="text-gray-300 mb-4">
              {importResults && importResults.filesImported > 0 ? (
                <>
                  Successfully imported <strong>{importResults.filesImported}</strong> commission files.
                  {importResults.filesDuplicate > 0 && (
                    <> ({importResults.filesDuplicate} duplicates skipped)</>
                  )}
                </>
              ) : (
                <>Your data has been processed and your dashboard is ready.</>
              )}
            </p>
            <p className="text-gray-400 text-sm mb-6">
              Your commission data has been processed. Everything is ready to go.
            </p>
            <button
              onClick={handleContinue}
              className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all"
            >
              Go to Dashboard
              <ArrowRight size={20} />
            </button>
          </div>
        )}

        {/* STATE: Waiting for data processing (files imported, pipeline running) */}
        {isWaitingForProcessing && (
          <div className="bg-amber-900/20 border border-amber-500/30 rounded-2xl p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 mb-4">
              <Clock className="text-amber-400" size={40} />
            </div>
            <h2 className="text-2xl font-bold text-amber-300 mb-2">
              Files Received — Under Review
            </h2>
            <p className="text-gray-300 mb-4">
              {importResults && importResults.filesImported > 0 ? (
                <>
                  <strong>{importResults.filesImported}</strong> commission {importResults.filesImported === 1 ? 'file has' : 'files have'} been uploaded successfully.
                  {importResults.filesDuplicate > 0 && (
                    <> ({importResults.filesDuplicate} duplicates skipped)</>
                  )}
                </>
              ) : (
                <>Your files have been uploaded successfully.</>
              )}
            </p>
            <p className="text-gray-400 text-sm mb-6">
              Our team is processing your data and building your dashboard.
              You will receive an email when your dashboard is ready.
              This usually takes less than 24 hours.
            </p>
            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 text-left">
              <p className="text-xs text-gray-500">
                We are analysing your commission data, calculating broker performance,
                identifying trail patterns, and building your personalised dashboard.
                You will be automatically redirected once approved.
              </p>
            </div>
          </div>
        )}

        {/* STATE: Data processed or staged, waiting for founder review */}
        {isWaitingForReview && (
          <div className="bg-amber-900/20 border border-amber-500/30 rounded-2xl p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 mb-4">
              <Clock className="text-amber-400" size={40} />
            </div>
            <h2 className="text-2xl font-bold text-amber-300 mb-2">
              Files Received — Under Review
            </h2>
            <p className="text-gray-300 mb-4">
              {isStagingMode ? (
                <>
                  Your commission files have been uploaded successfully.
                  Our team is setting up your data template and will process your files shortly.
                </>
              ) : (
                <>
                  Your data has been uploaded and processed successfully.
                  Our team is reviewing your files to ensure everything is correct.
                </>
              )}
            </p>
            <p className="text-gray-400 text-sm mb-6">
              You will receive an email when your dashboard is ready.
              This usually takes less than 24 hours.
            </p>
            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 text-left">
              <p className="text-xs text-gray-500">
                {isStagingMode ? (
                  <>
                    We are inspecting your commission files, building a processing template
                    to match your aggregator format, and will activate your dashboard once
                    the data has been verified. You will be automatically redirected once approved.
                  </>
                ) : (
                  <>
                    We verify the data quality, check for any anomalies, and ensure your
                    dashboard metrics are accurate before activating your account.
                    You will be automatically redirected once approved.
                  </>
                )}
              </p>
            </div>
          </div>
        )}

        {/* STATE: Import method selection (no import started yet) */}
        {!importComplete && companyId && (
          <>
            {/* Trust & Security Section */}
            <div className="bg-gray-800/50 border border-gray-700 rounded-2xl p-6 mb-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Shield size={20} className="text-emerald-400" />
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-white mb-3">
                    Your Data Security
                  </h2>
                  <ul className="space-y-2.5 text-sm text-gray-300">
                    <li className="flex items-start gap-2.5">
                      <CheckCircle size={16} className="text-emerald-400 flex-shrink-0 mt-0.5" />
                      <span>
                        Email scanning runs <strong className="text-white">entirely in your browser</strong> — nothing
                        is sent to our servers except the commission file attachments you approve
                      </span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <CheckCircle size={16} className="text-emerald-400 flex-shrink-0 mt-0.5" />
                      <span>
                        We request <strong className="text-white">read-only</strong> access to your Google Workspace or Gmail account — we cannot send, delete,
                        or modify any emails
                      </span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <CheckCircle size={16} className="text-emerald-400 flex-shrink-0 mt-0.5" />
                      <span>
                        Access token <strong className="text-white">expires in 1 hour</strong> and is never stored on our servers
                      </span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <CheckCircle size={16} className="text-emerald-400 flex-shrink-0 mt-0.5" />
                      <span>
                        We only search for emails from known commission senders and files
                        matching aggregator patterns
                      </span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <CheckCircle size={16} className="text-emerald-400 flex-shrink-0 mt-0.5" />
                      <span>
                        You <strong className="text-white">review and approve every file</strong> before import
                      </span>
                    </li>
                  </ul>
                  <div className="mt-4 flex items-center gap-4 text-xs">
                    <a
                      href={LEGAL_URLS.privacy}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-300 inline-flex items-center gap-1"
                    >
                      Privacy Policy <ExternalLink size={12} />
                    </a>
                    <a
                      href={LEGAL_URLS.terms}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-300 inline-flex items-center gap-1"
                    >
                      Terms of Service <ExternalLink size={12} />
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Two Import Options */}
            {activeMethod === null && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                {/* Option A: Google Workspace Import */}
                <button
                  onClick={() => setActiveMethod('gmail')}
                  className="group bg-gray-800/50 border border-gray-700 rounded-2xl p-6 text-left hover:border-blue-500/50 hover:bg-gray-800/70 transition-all"
                >
                  <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center mb-4 group-hover:bg-blue-500/30 transition-colors">
                    <Mail size={24} className="text-blue-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-1">
                    Google Workspace Import
                  </h3>
                  <p className="text-sm text-gray-400 mb-3">
                    Automatically scan your business email for commission files.
                    Works with Google Workspace, Gmail, Loan Market, AFG, WLTH, and Orde.
                  </p>
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-400 bg-blue-500/10 px-2.5 py-1 rounded-full">
                    Recommended
                  </span>
                </button>

                {/* Option B: Manual Upload (inline) */}
                <button
                  onClick={() => setActiveMethod('upload')}
                  className="group bg-gray-800/50 border border-gray-700 rounded-2xl p-6 text-left hover:border-indigo-500/50 hover:bg-gray-800/70 transition-all"
                >
                  <div className="w-12 h-12 rounded-xl bg-indigo-500/20 flex items-center justify-center mb-4 group-hover:bg-indigo-500/30 transition-colors">
                    <FileUp size={24} className="text-indigo-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-1">
                    Upload Files
                  </h3>
                  <p className="text-sm text-gray-400 mb-3">
                    Upload commission files directly from your computer. Supports
                    CSV, XLSX, XLS, and PDF files up to 50 MB each.
                  </p>
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-400 bg-gray-700/50 px-2.5 py-1 rounded-full">
                    Manual
                  </span>
                </button>
              </div>
            )}

            {/* Google Workspace Import — Expanded */}
            {activeMethod === 'gmail' && (
              <div className="space-y-4 mb-6">
                {/* Known & Custom Sender Emails */}
                <div className="bg-gray-800/50 border border-gray-700 rounded-2xl p-5">
                  <div className="flex items-start gap-3 mb-3">
                    <Mail size={18} className="text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-gray-200">
                        Sender addresses we search for
                      </h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        We scan your inbox for emails from these known aggregator addresses.
                        Files matching commission patterns are also detected regardless of sender.
                      </p>
                    </div>
                  </div>

                  {/* Known senders displayed as chips */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {KNOWN_COMMISSION_SENDERS.map((email) => (
                      <span
                        key={email}
                        className="inline-flex items-center text-xs bg-gray-700/60 text-gray-300 px-2.5 py-1 rounded-full"
                      >
                        {email}
                      </span>
                    ))}
                    {customEmails.map((email) => (
                      <span
                        key={email}
                        className="inline-flex items-center gap-1.5 bg-blue-500/15 text-blue-300 text-xs px-2.5 py-1 rounded-full"
                      >
                        {email}
                        <button
                          onClick={() => handleRemoveEmail(email)}
                          className="text-blue-400 hover:text-white transition-colors"
                        >
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                  </div>

                  {/* Add custom sender */}
                  <p className="text-xs text-gray-500 mb-2">
                    Does your aggregator use a different email address? Add it below.
                  </p>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={emailInput}
                      onChange={(e) => {
                        setEmailInput(e.target.value);
                        setEmailInputError(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddEmail();
                        }
                      }}
                      placeholder="commissions@example.com.au"
                      className="flex-1 bg-gray-900/50 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                    <button
                      onClick={handleAddEmail}
                      className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm text-gray-300 hover:bg-gray-600 hover:text-white transition-colors flex items-center gap-1"
                    >
                      <Plus size={16} />
                      Add
                    </button>
                  </div>
                  {emailInputError && (
                    <p className="mt-1.5 text-xs text-red-400">{emailInputError}</p>
                  )}
                </div>

                {/* Email Scout Scanner */}
                <div className="bg-gray-800/50 border border-gray-700 rounded-2xl overflow-hidden">
                  <div className="p-5 border-b border-gray-700 flex items-center justify-between">
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                      <Mail size={20} className="text-blue-400" />
                      Google Workspace Import
                    </h2>
                    <button
                      onClick={() => setActiveMethod(null)}
                      className="text-sm text-gray-500 hover:text-gray-300 transition-colors"
                    >
                      Back to options
                    </button>
                  </div>
                  <div className="p-5">
                    <EmailScoutScanner
                      companyId={companyId}
                      officeId={headOfficeId || undefined}
                      customSenderEmails={customEmails}
                      stagingMode={true} // Always stage from activation — founder assigns templates
                      onComplete={handleImportComplete}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Inline Upload — Expanded */}
            {activeMethod === 'upload' && (
              <div className="space-y-4 mb-6">
                <div className="bg-gray-800/50 border border-gray-700 rounded-2xl overflow-hidden">
                  <div className="p-5 border-b border-gray-700 flex items-center justify-between">
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                      <FileUp size={20} className="text-indigo-400" />
                      Upload Commission Files
                    </h2>
                    <button
                      onClick={() => {
                        setActiveMethod(null);
                        setUploadFiles([]);
                      }}
                      className="text-sm text-gray-500 hover:text-gray-300 transition-colors"
                    >
                      Back to options
                    </button>
                  </div>

                  <div className="p-5 space-y-4">
                    {/* Drop zone */}
                    <div
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      onClick={() => fileInputRef.current?.click()}
                      className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                        isDragOver
                          ? 'border-indigo-400 bg-indigo-500/10'
                          : 'border-gray-600 hover:border-gray-500 hover:bg-gray-800/30'
                      }`}
                    >
                      <Upload className="mx-auto text-gray-500 mb-3" size={32} />
                      <p className="text-sm text-gray-300 mb-1">
                        Drag and drop files here, or <span className="text-indigo-400">browse</span>
                      </p>
                      <p className="text-xs text-gray-500">
                        CSV, XLSX, XLS, or PDF — up to 50 MB each
                      </p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept=".csv,.xlsx,.xls,.pdf"
                        onChange={handleFileInputChange}
                        className="hidden"
                      />
                    </div>

                    {/* File list */}
                    {uploadFiles.length > 0 && (
                      <div className="space-y-2">
                        {uploadFiles.map((entry, index) => (
                          <div
                            key={`${entry.file.name}-${index}`}
                            className="flex items-center gap-3 bg-gray-900/40 border border-gray-700 rounded-lg px-3 py-2"
                          >
                            <File size={16} className="text-gray-500 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-gray-200 truncate">{entry.file.name}</p>
                              <p className="text-xs text-gray-500">
                                {(entry.file.size / 1024).toFixed(0)} KB
                              </p>
                            </div>
                            {entry.status === 'pending' && (
                              <button
                                onClick={() => removeFile(index)}
                                className="text-gray-500 hover:text-red-400 transition-colors"
                              >
                                <X size={16} />
                              </button>
                            )}
                            {entry.status === 'uploading' && (
                              <Loader2 size={16} className="animate-spin text-indigo-400" />
                            )}
                            {entry.status === 'done' && (
                              <CheckCircle size={16} className="text-green-400" />
                            )}
                            {entry.status === 'error' && (
                              <span className="text-xs text-red-400" title={entry.error}>
                                <AlertTriangle size={16} className="text-red-400" />
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Upload button */}
                    {pendingFileCount > 0 && (
                      <button
                        onClick={handleUploadAll}
                        disabled={isUploading || !headOfficeId}
                        className="w-full py-3 bg-gradient-to-r from-indigo-500 to-blue-600 text-white font-semibold rounded-lg hover:from-indigo-600 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {isUploading ? (
                          <>
                            <Loader2 size={18} className="animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload size={18} />
                            Upload {pendingFileCount} {pendingFileCount === 1 ? 'File' : 'Files'}
                          </>
                        )}
                      </button>
                    )}

                    {!headOfficeId ? (
                      <p className="text-xs text-amber-400 text-center">
                        Setting up your account... Please wait a moment.
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>
            )}

            {/* Skip / Upload Later — for white-glove companies */}
            {hasSubscription === false && (
              <div className="text-center">
                <button
                  onClick={() => setShowSkipWarning(true)}
                  className="text-gray-500 hover:text-gray-300 text-sm transition-colors"
                >
                  Upload later — skip to dashboard
                </button>
              </div>
            )}

            {/* For self-service companies, show info about data requirement */}
            {hasSubscription === true && activeMethod === null && (
              <div className="p-4 bg-indigo-900/20 border border-indigo-500/20 rounded-lg">
                <div className="flex items-start gap-3">
                  <Upload size={18} className="text-indigo-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-gray-400">
                    Your dashboard will be built automatically once your commission files
                    are imported and processed. Choose an import method above to get started.
                  </p>
                </div>
              </div>
            )}
          </>
        )}

        {/* Skip Warning Modal — only for white-glove companies */}
        {showSkipWarning && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 border border-gray-700 rounded-2xl max-w-md w-full p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="text-yellow-400" size={24} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    Skip Data Import?
                  </h3>
                  <p className="text-gray-400 text-sm mb-4">
                    Your dashboard will be <strong>empty</strong> until you upload commission data manually.
                    You can always import via email later from Settings.
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowSkipWarning(false)}
                      className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                    >
                      Go Back
                    </button>
                    <button
                      onClick={handleSkipForWhiteGlove}
                      className="flex-1 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      Skip Anyway
                    </button>
                  </div>
                </div>
                <button
                  onClick={() => setShowSkipWarning(false)}
                  className="text-gray-500 hover:text-white transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivationPage;
